/**
 * Dev Settings API Client
 * Handles communication with the project-service dev-settings endpoints.
 *
 * Simplified schema: ~15 fields (down from 47).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const PROJECTS_API_BASE = '/api/projects';

// ============================================================================
// Types
// ============================================================================

export type BranchScope = 'per-sprint' | 'per-epic';
export type MachineSize = 'shared-cpu-1x' | 'shared-cpu-2x' | 'shared-cpu-4x' | 'shared-cpu-8x';
export type ModelPreference = 'haiku' | 'sonnet' | 'opus';
export type VerifyCheck = 'lint' | 'typecheck' | 'test' | 'build';
export type BuildExecutionMode = 'code-only' | 'full-cycle';

export interface DevSettings {
  // Branching
  base_branch: string;
  branch_scope: BranchScope;

  // Sprites
  max_concurrent_sprites: number;
  sprite_max_lifetime_minutes: number;
  sprite_machine_size: MachineSize;
  model_preference: ModelPreference;

  // Quality
  verify_checks: VerifyCheck[];

  // PRs
  pr_auto_create: boolean;
  pr_draft_by_default: boolean;

  // Agent
  additional_instructions: string;

  // API Keys
  anthropic_api_key?: string;
  anthropic_api_key_fallbacks?: string[];

  // Full-cycle build config
  build_execution_mode?: BuildExecutionMode;

  // Metadata
  project_id: string;
  created_at: string;
  updated_at: string;
}

export type DevSettingsUpdate = Partial<Omit<DevSettings, 'project_id' | 'created_at' | 'updated_at'>>;

// ============================================================================
// API Functions
// ============================================================================

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(endpoint, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || response.statusText);
  }

  if (response.status === 204) return null as T;
  return response.json();
}

/**
 * Get dev settings for a project
 */
export async function getDevSettings(projectId: string): Promise<DevSettings> {
  return request<DevSettings>(`${PROJECTS_API_BASE}/${projectId}/dev-settings`);
}

/**
 * Update dev settings for a project
 */
export async function updateDevSettings(
  projectId: string,
  settings: DevSettingsUpdate
): Promise<DevSettings> {
  return request<DevSettings>(`${PROJECTS_API_BASE}/${projectId}/dev-settings`, {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });
}

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Hook to fetch dev settings
 */
export function useDevSettings(projectId: string | undefined) {
  return useQuery({
    queryKey: ['dev-settings', projectId],
    queryFn: () => getDevSettings(projectId!),
    enabled: !!projectId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to update dev settings
 */
export function useUpdateDevSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, settings }: { projectId: string; settings: DevSettingsUpdate }) =>
      updateDevSettings(projectId, settings),
    onSuccess: (data) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['dev-settings', data.project_id] });
    },
  });
}
